import { LookupTrackerStore, Model, QueryParams } from "@ofqual/portal-core/core/types";

type NullableNumber = number | null;

export type Scope = {
  id: string;
  title: string;
  organisationID: number;
  organisationName: string;
  inclusionExclusion: NullableNumber;
  recognitionPermissionType: NullableNumber;
  qualificationType: NullableNumber;
  level: NullableNumber;
  sectorSubjectArea: NullableNumber;
  apprenticeshipStandard: NullableNumber;
  tLevelSubject: NullableNumber;
  qualificationDescription: string;
  status: ApplicationScopeStatus;
  shouldDelete: boolean;
  validStartDate: string | null;
  dirtyFields?: string[];
  active: boolean;
};

export enum ApplicationScopeStatus {
  NotSet = 0,
  Accepted = 1,
  Rejected = 2,
}

export const defaultValues: Partial<Scope> = {
  inclusionExclusion: null,
  recognitionPermissionType: null,
  qualificationType: null,
  level: null,
  sectorSubjectArea: null,
  apprenticeshipStandard: null,
  tLevelSubject: null,
  qualificationDescription: "",
  validStartDate: null,
};

export const stringStatusValues = [
  { status: ApplicationScopeStatus.NotSet, stringValue: "" },
  { status: ApplicationScopeStatus.Accepted, stringValue: "accepted" },
  { status: ApplicationScopeStatus.Rejected, stringValue: "rejected" },
];

export const getStatusStringValue = (status: ApplicationScopeStatus) =>
  stringStatusValues.find((x) => x.status === status)?.stringValue;

export const getStatusNumberValue = (statusStr: string) =>
  stringStatusValues.find((x) => x.stringValue === statusStr)!.status;

//TODO: Scope API should be refactored so view model is the ids for the refdata items
// this mapping code could then be removed
export const toApiModel = (scope: Scope, itemId: string) => {
  return {
    ...scope,
    recognitionPermissionType: scope.recognitionPermissionType
      ? { id: scope.recognitionPermissionType }
      : null,
    qualificationType: scope.qualificationType
      ? { id: scope.qualificationType }
      : null,
    level: scope.level ? { id: scope.level } : null,
    sectorSubjectArea: scope.sectorSubjectArea
      ? { id: scope.sectorSubjectArea }
      : null,
    apprenticeshipStandard: scope.apprenticeshipStandard
      ? { id: scope.apprenticeshipStandard }
      : null,
    tLevelSubject: scope.tLevelSubject ? { id: scope.tLevelSubject } : null,
    organisationID: +itemId,
  } as Model;
};

export const toOrgApiModel = (scope: Scope) => {
  return {
    ...scope,
    recognitionPermissionTypeId: scope.recognitionPermissionType || 0,
    qualificationTypeId: scope.qualificationType || 0,
    levelId: scope.level || 0,
    sectorSubjectId: scope.sectorSubjectArea || 0,
    apprenticeshipStandardId: scope.apprenticeshipStandard || 0,
    tLevelSubjectId: scope.tLevelSubject || 0,
  };
};

export const fromOrgApiModel = (model: Model): Scope => {
  return {
    ...model,
    recognitionPermissionType: model.recognitionPermissionTypeID,
    qualificationType: model.qualificationTypeID,
    level: model.levelID,
    sectorSubjectArea: model.sectorSubjectAreaID,
    apprenticeshipStandard: model.apprenticeshipStandardID,
    tLevelSubject: model.tLevelSubjectID,
  } as Scope;
};

export const fromApiModel = (model: Model): Scope => {
  return {
    ...model,
    recognitionPermissionType: model.recognitionPermissionType
      ? model.recognitionPermissionType.id
      : null,
    qualificationType: model.qualificationType
      ? model.qualificationType.id
      : null,
    level: model.level ? model.level.id : null,
    sectorSubjectArea: model.sectorSubjectArea
      ? model.sectorSubjectArea.id
      : null,
    apprenticeshipStandard: model.apprenticeshipStandard
      ? model.apprenticeshipStandard.id
      : null,
    tLevelSubject: model.tLevelSubject ? model.tLevelSubject.id : null,
  } as Scope;
};

export const generateTitle = (
  scope: Scope,
  lookupTracking: LookupTrackerStore
): string => {
  const epaQualType = 25;
  const technicalQualType = 31;
  let title = "";

  const ssaDesc =
    scope.sectorSubjectArea !== null &&
    lookupTracking.sectorSubjectArea[scope.sectorSubjectArea];
  const qualTypeDesc =
    scope.qualificationType !== null &&
    lookupTracking.qualificationType[scope.qualificationType];
  const levelDesc = scope.level !== null && lookupTracking.level[scope.level];
  const permTypeDesc =
    scope.recognitionPermissionType !== null &&
    lookupTracking.recognitionPermissionType[scope.recognitionPermissionType];
  const appStandardDesc =
    scope.apprenticeshipStandard !== null &&
    lookupTracking.apprenticeshipStandard[scope.apprenticeshipStandard];
  const tLevelDesc =
    scope.tLevelSubject !== null &&
    lookupTracking.tLevelSubject[scope.tLevelSubject];
  const inclusionDesc =
    scope.inclusionExclusion !== null &&
    lookupTracking.inclusionExclusion[scope.inclusionExclusion];

  // scope Regulatory Qualification Type that is not EPA or TQ
  if (
    scope.recognitionPermissionType === 1 &&
    scope.qualificationType !== epaQualType &&
    scope.qualificationType !== technicalQualType
  )
    return (title = `${inclusionDesc}\\${ssaDesc}\\${qualTypeDesc}\\${levelDesc}\\${permTypeDesc}`);

  // EPA
  if (
    scope.recognitionPermissionType === 1 &&
    scope.qualificationType === epaQualType
  )
    return (title = `${inclusionDesc}\\${appStandardDesc}\\${qualTypeDesc}\\${levelDesc}\\${permTypeDesc}`);

  // TQ
  if (
    scope.recognitionPermissionType === 1 &&
    scope.qualificationType === technicalQualType
  )
    return (title = `${inclusionDesc}\\${tLevelDesc}\\${qualTypeDesc}\\${levelDesc}\\${permTypeDesc}`);

  // All qualifications subject only to the General Conditions of Recognition
  if (scope.recognitionPermissionType === 2)
    return (title = `${inclusionDesc}\\${ssaDesc}\\${levelDesc}\\${permTypeDesc}`);

  // specific qualification description (inclusion or exclusion)
  if (scope.recognitionPermissionType === 3 || scope.inclusionExclusion === 0)
    return (title = `${inclusionDesc}\\${scope.qualificationDescription}\\${permTypeDesc}`);

  return title;
};

export const isDirty = (scopes: Scope[]) =>
  scopes.some((scope) => scope.shouldDelete || scope.dirtyFields?.length);


export const searchFilterScopes = (searchParams: QueryParams, scopes: Scope[]) => {
    let currentScopes = scopes;
    const searchParamKeys = Object.keys(searchParams);
    const searchKey = searchParamKeys.find((x) => x === "search");
    const filterKeys = searchParamKeys.filter((x) => x !== searchKey);
    if (searchKey) {
      const textArray = searchParams[searchKey].split(/\s|\/|\\|,|;|:|-/);
      currentScopes = currentScopes.filter((scope) =>
        textArray.every((chunk: string) =>
          scope.title.toLowerCase().includes(chunk.toLowerCase())
        )
      );
    }
    currentScopes = currentScopes.filter((scope) => {
      return (filterKeys as (keyof Scope)[]).every(
        (searchedForKey) =>
          scope[searchedForKey] === searchParams[searchedForKey]
      );
    });
    return currentScopes;
};